import { CSSProperties, FC, ReactNode, useCallback, useEffect, useMemo, useState } from "react"

import { useHistory } from "react-router-dom"
import { Alert, AutoComplete, Button, Col, DatePicker, Input, Radio, Row, Switch } from "antd"

import dayjs from "dayjs"
import { Translated } from "../../utils/translated"
import { FlexCol, FlexRow } from "../../components/commons-ts/common"
import { ErrorAlert } from "../../components/commons-ts/errorAlert"
import { MethodTypeEN1177, NormType, Site } from "../../generated/proto-ts/main"
import { callCloudApiV2, getLocalStorageCloudApiUser } from "../../utils/cloudApiV2"
import {
  COLOR_BG_GRAY,
  COLOR_BG_LIGHT_BLUE,
  colorHexToRgba,
  pbUUIDToUuid,
  uuidToPbUUID,
} from "../../utils/utils"
import { useUsercommCreateSiteBimodal } from "../../usercomm/common/usercommAsyncRequestBimodal"
import { v4 as uuidv4 } from "uuid"
import { ICloudCustomer } from "../../types"
import { useSyncronizationContext } from "../../providers/syncronizationProvider"
import { GoogleAddressWidget } from "../../components/commons-ts/googleMaps"
import { normalizeString } from "../../utils/users"
import { PlusSquareOutlined } from "@ant-design/icons"

const SimplifiedCustomerCreateForm: FC<{
  name: string
  contact: string
  setContact: (contact: string) => void
  address: string
  setAddress: (address: string) => void
}> = ({ name, contact, setContact, address, setAddress }) => {
  return (
    <div
      style={{
        padding: "10px",
        borderRadius: "10px",
        backgroundColor: colorHexToRgba(COLOR_BG_LIGHT_BLUE, 0.2),
      }}
    >
      <Row gutter={[10, 0]} justify="space-between" align="top">
        <Col xs={24}>
          <Translated keyEn="New customer" />
        </Col>
        <Col xs={24} md={8}>
          <FlexCol>
            <Translated keyEn="Name" />
            <Input disabled size="large" value={name} />
          </FlexCol>
        </Col>
        <Col xs={24} md={8}>
          <FlexCol>
            <Translated keyEn="Contact" />
            <Input
              size="large"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder="Customer Contact"
            />
          </FlexCol>
        </Col>
        <Col xs={24} md={8}>
          <FlexCol>
            <Translated keyEn="Address" />
            <Input
              size="large"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Customer Address"
            />
          </FlexCol>
        </Col>
      </Row>
    </div>
  )
}

export const SiteInitForm: FC<{
  sites: Site[] | null
  onToggle: (state: boolean) => void
}> = ({ sites, onToggle }) => {
  const [missionNormType, setMissionNormType] = useState<NormType>(NormType.EN_1177)
  const [missionMethod, setMissionMethod] = useState<MethodTypeEN1177 | null>(
    MethodTypeEN1177.CRITICAL_FALL_HEIGHT_DETERMINATION,
  )
  const [missionName, setMissionName] = useState<string>("")
  const [clientName, setClientName] = useState<string>("")
  const [siteName, setSiteName] = useState<string>("")
  const [address, setAddress] = useState<string | null>("")
  const [isLabTest, setIsLabTest] = useState<boolean | null>(false) // DEPRECATED
  const [executionDate, setExecutionDate] = useState<dayjs.Dayjs | null>(dayjs())

  const [userUUID, setUserUUID] = useState<string | null>(null)

  const [createdSiteUUID, createSiteRequest] = useUsercommCreateSiteBimodal()

  const { currentEnterprise } = useSyncronizationContext()
  const [enterpriseCustomers, setEnterpriseCustomers] = useState<ICloudCustomer[]>([])

  const [shouldCreateCustomer, setShouldCreateCustomer] = useState<boolean>(false)
  const [newCustomerContact, setNewCustomerContact] = useState<string>("")
  const [newCustomerAddress, setNewCustomerAddress] = useState<string>("")

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const history = useHistory()

  const upsertCustomerCB = useCallback(
    async (customer: ICloudCustomer) => {
      if (currentEnterprise === null) {
        console.error(`EnterpriseSettings: upsertCustomerCB: currentEnterprise is null`)
        return
      }
      let { entity } = await callCloudApiV2(`/customer`, {
        method: "PATCH",
        body: JSON.stringify({
          ...customer,
          EnterpriseUUID: currentEnterprise.UUID,
        }),
      })
      console.log(`EnterpriseSettings: upsertCustomerCB: entity`, entity)
    },
    [currentEnterprise],
  )

  const handleCreateCustomerCB = useCallback(async () => {
    if (!shouldCreateCustomer) {
      return
    }
    if (currentEnterprise === null) {
      console.warn(
        `EnterpriseSettings: handleCreateCustomerCB: currentEnterprise is null: cannot create customer`,
      )
      return
    }
    let customer: ICloudCustomer = {
      UUID: uuidv4(),
      Name: clientName,
      Contact: newCustomerContact,
      Email: "",
      PhoneNumber: "",
      PrimaryAddress: newCustomerAddress,
      EnterpriseUUID: currentEnterprise.UUID,
    }
    await upsertCustomerCB(customer)
  }, [
    currentEnterprise,
    clientName,
    shouldCreateCustomer,
    newCustomerContact,
    newCustomerAddress,
    upsertCustomerCB,
  ])

  useEffect(() => {
    let user = getLocalStorageCloudApiUser()
    if (user !== null && user.UUID !== null) {
      setUserUUID(user.UUID)
    }
  }, [])

  const onSubmit = useCallback(async () => {
    if (userUUID === null) {
      setError("User UUID is required")
      return
    }
    if (siteName === "") {
      setError("Site name is required")
      return
    }

    setIsLoading(true)
    setError(null)

    // Create customer if needed
    try {
      await handleCreateCustomerCB()
    } catch (e: any) {
      console.error("SiteInitForm: error creating customer: ", e)
    }

    let method_type = missionMethod
    let isLabTest: boolean | null = null
    if (missionNormType === NormType.EN_12503) {
      method_type = null
      isLabTest = null
    }
    let ts = Date.now()
    let newSite = new Site({
      uuid: uuidToPbUUID(uuidv4()),
      norm_type: missionNormType,
      method_type_en_1177: method_type ?? undefined,
      is_lab_test: isLabTest ?? undefined,
      mission_name: missionName,
      client_name: clientName,
      site_name: siteName,
      address: address ?? "",
      created_at: ts,
      updated_at: ts,
    })
    if (executionDate !== null) {
      newSite.execution_date = executionDate?.toDate().getTime()
    }
    createSiteRequest(userUUID, newSite)
  }, [
    missionNormType,
    missionMethod,
    missionName,
    clientName,
    siteName,
    isLabTest,
    executionDate,
    address,
    userUUID,

    handleCreateCustomerCB,
  ])

  useEffect(() => {
    if (createdSiteUUID === null) {
      return
    }
    setIsLoading(false)
    history.push(`/sites/${pbUUIDToUuid(createdSiteUUID)}`)
  }, [createdSiteUUID])

  // Cloud: Enterprise customers
  const loadEnterpriseCustomersCB = useCallback(async () => {
    if (currentEnterprise === null) {
      console.warn(
        `EnterpriseSettings: currentEnterprise is null, skipping loading enterprise customers`,
      )
      return
    }
    let { entity: enterpriseCustomers } = await callCloudApiV2<ICloudCustomer[]>(
      `/enterprise/customers?enterprise_uuid=${currentEnterprise.UUID}`,
    )
    if (enterpriseCustomers === null) {
      console.error(
        `EnterpriseSettings: error loading enterprise customers: enterpriseCustomers is null`,
      )
      return
    }
    console.log(`EnterpriseSettings: successfully loaded enterprise customers`, enterpriseCustomers)
    setEnterpriseCustomers(enterpriseCustomers)
  }, [currentEnterprise])

  useEffect(() => {
    loadEnterpriseCustomersCB()
  }, [currentEnterprise])

  const memoSiteNameOptions = useMemo(() => {
    if (sites === null) {
      return []
    }
    let siteNameSet: Set<string> = new Set()
    for (let site of sites) {
      if (site.site_name !== "") {
        siteNameSet.add(site.site_name)
      }
    }
    let siteNames = Array.from(siteNameSet)
    siteNames.sort((a, b) => a.localeCompare(b))
    let siteNameOptions = siteNames.map((name) => ({ value: name }))
    return siteNameOptions
  }, [sites])

  const memoMissionNameOptions = useMemo(() => {
    if (sites === null) {
      return []
    }
    let missionNameSet: Set<string> = new Set()
    for (let site of sites) {
      if (site.mission_name !== "") {
        missionNameSet.add(site.mission_name)
      }
    }
    let missionNames = Array.from(missionNameSet)
    missionNames.sort((a, b) => a.localeCompare(b))
    let missionNameOptions = missionNames.map((name) => ({ value: name }))
    return missionNameOptions
  }, [sites])

  const memoCustomerNameOptions = useMemo(() => {
    if (enterpriseCustomers === null) {
      return []
    }
    let customerNameSet: Set<string> = new Set()
    for (let customer of enterpriseCustomers) {
      if (customer.Name !== "") {
        customerNameSet.add(customer.Name)
      }
    }
    let customerNames = Array.from(customerNameSet)
    customerNames.sort((a, b) => a.localeCompare(b))
    let customerNameOptions = customerNames.map((name) => ({ value: name }))
    return customerNameOptions
  }, [enterpriseCustomers])

  const memoClientAddress = useMemo(() => {
    if (enterpriseCustomers === null || clientName === "") {
      return ""
    }
    let selectedCustomer = enterpriseCustomers.find((c) => c.Name === clientName)
    if (selectedCustomer === undefined) {
      return ""
    }
    return selectedCustomer.PrimaryAddress
  }, [clientName, enterpriseCustomers])

  const memoMissionNormTypeExplicationElement = useMemo(() => {
    let explicationAlert: ReactNode = null
    let logoSrc = ""
    if (missionNormType === NormType.EN_1177) {
      explicationAlert = (
        <Alert
          style={{ height: "100%" }}
          message={
            <>
              <Translated keyEn="European norm" />{" "}
              <b>
                <a
                  target="_blank"
                  href="https://www.boutique.afnor.org/en-gb/standard/nf-en-1177-in1/impact-attenuating-playground-surfacing-methods-of-test-for-determination-o/fa203676/366543"
                >
                  EN 1177
                </a>
              </b>
              : <Translated keyEn="HIC values of playground floor elements" />
            </>
          }
          description={
            <>
              <Translated keyEn="The test is either performed in a laboratory or on site: please indicate" />
              <br />
              <Translated keyEn="2 methods are supported for calculating conformity result:" />
              <br />
              <ul>
                <li>
                  <Translated keyEn="1: CFH (Critical Fall Height)" />
                </li>
                <li>
                  <Translated keyEn="2: Adequacy (Impact Attenuation)" />
                </li>
              </ul>
              <Translated keyEn="Measured properties:" />
              <ul>
                <li>
                  <Translated keyEn="Gmax, g" />
                </li>
                <li>
                  <Translated keyEn="HIC" />
                </li>
              </ul>
            </>
          }
          type="info"
          showIcon
        />
      )
      logoSrc = "EN-1177-garcon-sols-synthetiques.jpg"
    } else if (missionNormType === NormType.EN_12503) {
      explicationAlert = (
        <Alert
          style={{ height: "100%" }}
          message={
            <>
              <Translated keyEn="European norm" />{" "}
              <b>
                <a
                  target="_blank"
                  href="https://www.boutique.afnor.org/en-gb/standard/nf-en-125034/sports-mats-part-4-determination-of-shock-absorption/fa063333/57540"
                >
                  EN 12503
                </a>
              </b>
              :{" "}
              <Translated keyEn="shock absorption of sports mats: gymnastics, pole vault, high jump and judo" />
              <br />
              <Translated keyEn="Also used for reception mats in climbing gyms:" />{" "}
              <b>
                <a
                  target="_blank"
                  href="https://www.boutique.afnor.org/en-gb/standard/nf-en-125722/artificial-climbing-structures-part-2-safety-requirements-and-test-methods-/fa182518/58638"
                >
                  EN 12572
                </a>
              </b>
            </>
          }
          description={
            <>
              <Translated keyEn="The test is performed in a laboratory on a" />{" "}
              <b>
                <Translated keyEn="specifically chosen" />
              </b>{" "}
              <Translated keyEn="type of sports mat" />
              <br />
              <Translated keyEn="Measured properties:" />
              <ul>
                <li>
                  <Translated keyEn="Gmax, g" />
                </li>
                <li>
                  <Translated keyEn="Resilience, %" />
                </li>
                <li>
                  <Translated keyEn="Deflection, % (or mm)" />
                </li>
              </ul>
            </>
          }
          type="info"
          showIcon
        />
      )
      logoSrc = "EN-12503-12572-garcon-sols-gymnastique.jpg"
    } else if (missionNormType === NormType.EN_14960) {
      explicationAlert = (
        <Alert
          style={{ width: "100%" }}
          message={
            <>
              <Translated keyEn="European norm" />{" "}
              <b>
                <a
                  target="_blank"
                  href="https://www.boutique.afnor.org/en-gb/standard/nf-en-149601/inflatable-play-equipment-part-1-safety-requirements-and-test-methods/fa191651/1806"
                >
                  EN 14960
                </a>
              </b>
              : <Translated keyEn='inflatable play equipment, aka "airbags"' />
            </>
          }
          description={
            <>
              <Translated keyEn="The test is performed in a laboratory on any type of airbag" />
              <br />
              <Translated keyEn="Measured properties:" />
              <ul>
                <li>
                  <Translated keyEn="Gmax, g" />
                </li>
                <li>
                  <Translated keyEn="Resilience, %" />
                </li>
                <li>
                  <Translated keyEn="Deflection, % (or mm)" />
                </li>
              </ul>
            </>
          }
          type="info"
          showIcon
        />
      )
      logoSrc = ""
    } else if (missionNormType === NormType.EN_ISO_23659) {
      explicationAlert = (
        <Alert
          style={{ height: "100%" }}
          message={
            <>
              <Translated keyEn="EN" />{" "}
              <b>
                <a target="_blank" href="https://www.iso.org/standard/76556.html">
                  ISO 23659:2022
                </a>
              </b>
              : <Translated keyEn="sports and recreational facilities — trampoline parks" />
            </>
          }
          description={
            <>
              <Translated keyEn="The test is performed in laboratory or on-site on any type of trampoline" />
              <br />
              <Translated keyEn="Measured properties:" />
              <ul>
                <li>
                  <Translated keyEn="Gmax, g" />
                </li>
                <li>
                  <Translated keyEn="Rebounce height, m" />
                </li>
                <li>
                  <Translated keyEn="Performance factor, [0-120]" />
                </li>
              </ul>
            </>
          }
          type="info"
          showIcon
        />
      )
      logoSrc = "EN-ISO-23569-garcon-protection-trampoline.jpg"
    }
    return (
      <Row justify="start" align="top" gutter={[10, 10]} style={{ minHeight: "220px" }}>
        <Col xs={24} md={18}>
          {explicationAlert}
        </Col>
        <Col xs={0} md={6}>
          {logoSrc !== "" && (
            <FlexRow
              style={{
                width: "100%",
                height: "100%",
                justifyContent: "end",
                alignItems: "center",
              }}
            >
              <img
                style={{
                  width: "200px",
                  height: "100%",
                  alignSelf: "center",
                }}
                src={"/" + logoSrc}
              />
            </FlexRow>
          )}
        </Col>
      </Row>
    )
  }, [missionNormType])

  let radioButtonStyle: CSSProperties = {
    textAlign: "center",
  }
  return (
    <FlexCol
      style={{
        width: "100%",
        height: "100%",
        justifyContent: "space-between",
        minHeight: "70vh",
      }}
    >
      <Row gutter={[10, 10]} justify={"space-between"}>
        <Col xs={24}>
          <ErrorAlert error={error} />
        </Col>
        <Col xs={24}>
          <Radio.Group
            value={missionNormType}
            buttonStyle="solid"
            size="large"
            style={{
              width: "100%",
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
            }}
            onChange={(e) => setMissionNormType(e.target.value)}
          >
            <Radio.Button value={NormType.EN_1177} style={radioButtonStyle}>
              <Translated keyEn="Playground Floor" />
            </Radio.Button>
            <Radio.Button value={NormType.EN_12503} style={radioButtonStyle}>
              <Translated keyEn="Sports Mats" />
            </Radio.Button>
            <Radio.Button value={NormType.EN_14960} style={radioButtonStyle}>
              <Translated keyEn="Airbags" />
            </Radio.Button>
            <Radio.Button value={NormType.EN_ISO_23659} style={radioButtonStyle}>
              <Translated keyEn="Trampolines" />
            </Radio.Button>
          </Radio.Group>
        </Col>
        <Col xs={0} md={24}>
          {memoMissionNormTypeExplicationElement}
        </Col>
        {missionNormType === NormType.EN_1177 && (
          <Col xs={24}>
            <FlexCol>
              <Translated keyEn="Method" />
              <Radio.Group
                buttonStyle="solid"
                size="large"
                value={missionMethod}
                onChange={(e) => setMissionMethod(e.target.value)}
              >
                <Radio.Button
                  style={{ width: "50%" }}
                  value={MethodTypeEN1177.CRITICAL_FALL_HEIGHT_DETERMINATION}
                >
                  1: <Translated keyEn="CFH" />
                </Radio.Button>
                <Radio.Button
                  style={{ width: "50%" }}
                  value={MethodTypeEN1177.IMPACT_ATTENUATION_COMPLIANCE}
                >
                  2: <Translated keyEn="Adequacy" />
                </Radio.Button>
              </Radio.Group>
            </FlexCol>
          </Col>
        )}
        <Col xs={24} md={6}>
          <FlexCol>
            Execution Date
            <DatePicker
              picker="date"
              variant="filled"
              size="large"
              value={executionDate}
              onChange={(date) => setExecutionDate(date)}
            />
          </FlexCol>
        </Col>
        <Col xs={24} md={6}>
          <FlexCol>
            <Translated keyEn="Mission" />
            <AutoComplete
              size="large"
              variant="filled"
              value={missionName}
              style={{ width: "100%" }}
              options={memoMissionNameOptions}
              onChange={(value) => {
                setMissionName(value)
              }}
              onSelect={(value) => {
                setMissionName(value)
              }}
              filterOption={(inputValue, option) =>
                normalizeString(option!.value).indexOf(normalizeString(inputValue)) !== -1
              }
            />
          </FlexCol>
        </Col>

        <Col xs={24} md={6}>
          <FlexCol>
            <Translated keyEn="Site" />
            <AutoComplete
              size="large"
              variant="filled"
              value={siteName}
              style={{ width: "100%" }}
              options={memoSiteNameOptions}
              onChange={(value) => {
                setSiteName(value)
              }}
              onSelect={(value) => {
                setSiteName(value)
              }}
              filterOption={(inputValue, option) =>
                normalizeString(option!.value).indexOf(normalizeString(inputValue)) !== -1
              }
            />
          </FlexCol>
        </Col>
        <Col xs={24} md={6}>
          <FlexCol>
            <Translated keyEn="Client" />
            <FlexRow style={{ width: "100%" }}>
              <FlexCol style={{ gap: 0, width: "100%" }}>
                <AutoComplete
                  size="large"
                  variant="filled"
                  value={clientName ?? ""}
                  style={{ width: "100%" }}
                  options={memoCustomerNameOptions}
                  onChange={(value) => {
                    setClientName(value)
                  }}
                  onSelect={(value) => {
                    let selectedCustomer = enterpriseCustomers.find((c) => c.Name === value)
                    if (selectedCustomer) {
                      setClientName(selectedCustomer.Name)
                    }
                  }}
                  filterOption={(inputValue, option) =>
                    normalizeString(option!.value).indexOf(normalizeString(inputValue)) !== -1
                  }
                />
                <div
                  style={{
                    fontStyle: "italic",
                    fontSize: "0.8rem",
                    color: "#888",
                  }}
                >
                  {memoClientAddress}
                </div>
              </FlexCol>
              <Button
                icon={<PlusSquareOutlined />}
                onClick={(e) => {
                  e.stopPropagation()
                  setShouldCreateCustomer(!shouldCreateCustomer)
                }}
              />
            </FlexRow>
          </FlexCol>
        </Col>
        {shouldCreateCustomer && (
          <Col xs={24}>
            <SimplifiedCustomerCreateForm
              name={clientName}
              contact={newCustomerContact}
              setContact={setNewCustomerContact}
              address={newCustomerAddress}
              setAddress={setNewCustomerAddress}
            />
          </Col>
        )}
        <Col xs={24}>
          <FlexCol>
            <Translated keyEn="Address" />
            <GoogleAddressWidget address={address} setAddress={setAddress} points={null} />
          </FlexCol>
        </Col>
      </Row>
      <FlexRow
        style={{
          alignItems: "center",
          justifyContent: "end",
        }}
      >
        <Button
          onClick={() => {
            onToggle(false)
          }}
          type="link"
        >
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={onSubmit}
          loading={isLoading}
          disabled={isLoading}
          size="large"
          style={{
            minWidth: 200,
          }}
        >
          Create
        </Button>
      </FlexRow>
    </FlexCol>
  )
}
